/**
 * The By phone kind of service location (service-by-phone, design D2, D3, D5,
 * D7). A service is In person, Online or By phone; the number to call is the
 * client's (`UserPii.phone`), never the service's, and is read where it is shown
 * or sent, never copied.
 *
 * Pure and zero-import: both barrels export it.
 */

export type ServiceLocationKind = 'IN_PERSON' | 'ONLINE' | 'PHONE';

/** The one answer to "where does this service happen". */
export function serviceLocationKind(service: {
  isVirtual?: boolean | null;
  isByPhone?: boolean | null;
}): ServiceLocationKind {
  if (service.isByPhone === true) return 'PHONE';
  return service.isVirtual === true ? 'ONLINE' : 'IN_PERSON';
}

/** The kind's plain name on every chip and Format fact. */
export const LOCATION_KIND_LABEL: Readonly<Record<ServiceLocationKind, string>> = {
  IN_PERSON: 'In person',
  ONLINE: 'Online',
  PHONE: 'By phone',
};

/**
 * What a page or message says about a by-phone booking. Null unless the
 * service is by phone. `caller` is worked out once on the server (the
 * provider, else the studio); `phone` is null when the client has no number.
 * The key is `phone` so the logger redacts it.
 */
export interface PhoneCall {
  caller: string;
  phone: string | null;
}

/**
 * D3: who calls. The provider's first and last name, as the Booked message names
 * them; with no provider, or a provider with no name, the studio's brand name.
 */
export function phoneCaller(
  provider: { firstName: string | null; lastName: string | null } | null | undefined,
  brandName: string,
): string {
  const name = provider ? [provider.firstName, provider.lastName].filter(Boolean).join(' ') : '';
  return name || brandName;
}

/** No number: missing, or blank after trimming (a stored blank included). */
export function hasPhoneToCall(phone: string | null | undefined): phone is string {
  return typeof phone === 'string' && phone.trim() !== '';
}

/** The number as it is printed and stored: trimmed, or null when there is none. */
export function phoneToCall(phone: string | null | undefined): string | null {
  return hasPhoneToCall(phone) ? phone.trim() : null;
}

/** "Dana Wilcher will call you at +1 555 010 0199." */
export function phoneCallLine(caller: string, phone: string | null | undefined): string {
  const number = phoneToCall(phone);
  return number ? `${caller} will call you at ${number}.` : `${caller} will call you.`;
}
